import * as React from "react";

import {useIntersectionObserver} from "../hooks";
import {ResponseProductDinkus} from "../model";

interface ProductImageSlideProps {
  src: string | undefined;
  alt: string | undefined;
  dinkus?: ResponseProductDinkus;
  isVisible?: boolean;
  numberOfColumnsToDisplay?: number;
}

const SpecialProductLoadingGraphic: React.FunctionComponent = () => {
  return (
    <div className="o-loading__loader">
      <div></div>
      <div></div>
      <div></div>
    </div>
  );
};

export const getImageAltFromUrl = (
    url: string | URL
) => {
  const urlObject = new URL(url);
  const fileName = urlObject.pathname.split('/').pop();
  if (fileName) {
    const cleanFileName = fileName.split('?')[0].split('#')[0];
    const decodedFileNameWithExt = decodeURIComponent(cleanFileName);
    const fileNameWithSpaces = decodedFileNameWithExt.replace(/\.[^/.]+$/, '');
    return fileNameWithSpaces.replace(/-/g, ' ');
  }

  return '';
};

export const ProductImageSlide: React.FunctionComponent<
  ProductImageSlideProps
> = (props) => {
  const [hasLoaded, setHasLoaded] = React.useState(false);

  const onImageLoaded = () => {
    setHasLoaded(true);
  };
  const onImageLoadingError = () => {
    setHasLoaded(true);
  };

  const modifyImageUrl = (url: string, size: number, quality: number) => {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    if (size) {
      params.set('width', size.toString());
      params.set('height', size.toString());
      params.set('canvas', `${size},${size}`);
    }

    if (quality) {
      params.set('quality', quality.toString());
    }

    return urlObj.toString();
  }

  const showImage = props.isVisible || hasLoaded;

  const srcSet = props.src ? `
    ${modifyImageUrl(props.src, 200, 80)} 200w,
    ${modifyImageUrl(props.src, 300, 80)} 300w,
    ${modifyImageUrl(props.src, 400, 80)} 400w,
    ${modifyImageUrl(props.src, 600, 80)} 600w
  ` : undefined;

  const mobileSize = () => {
    switch (props.numberOfColumnsToDisplay) {
      case 2:
        return "calc(50vw - 30px)";
      default:
        return "calc(100vw - 40px)";
    }
  }

  const dinkus = React.useMemo(() => {
    if (!props.dinkus) {
      return null;
    }
    return Object.entries(props.dinkus).map(([key, value]) => {
      if (!value || (value as string).length < 1) {
        return null;
      }

      const alt = getImageAltFromUrl(value);

      return <img key={key} className={`plp-dinkus plp-${key}`} alt={alt} src={value} width="76px" height="32px" />;
    });
  }, [props.dinkus]);

  return (
    <div className="bbnt-product-image-slide">
      {showImage && (
        <React.Fragment>
          <img
            src={modifyImageUrl(props.src, 300, 80)}
            srcSet={srcSet}
            sizes={`(min-width: 768px) 300px, ${mobileSize()}`}
            alt={props.alt}
            onLoad={onImageLoaded}
            onError={onImageLoadingError}
            width="300px"
            height="300px"
          />
        </React.Fragment>
      )}

      {dinkus}

      {showImage && (
        <React.Fragment>
          <div className="hovering-overlay" />
        </React.Fragment>
      )}

      {!hasLoaded && <SpecialProductLoadingGraphic />}
    </div>
  );
};
